// MobileStepper — compact step indicator that replaces the wide POS step nav
// in mobile mode. Renders just "Step 3 of 7 · Fabric" with a thin progress bar
// and prev/next chevrons to jump within already-visited steps.
//
// Visible only when body has the `mobile-mode` class (CSS handles the toggle).

import React from "react";
import { ChevronDown } from "lucide-react";

export const MobileStepper = ({
  steps = [],
  currentStep,
  stepOrder = [],
  onStepChange,
  testId = "mobile-stepper",
}) => {
  const currentIndex = Math.max(0, stepOrder.indexOf(currentStep));
  const total = stepOrder.length || steps.length || 1;
  const current = steps.find((s) => s.id === currentStep) || steps[currentIndex] || {};
  const progress = ((currentIndex + 1) / total) * 100;
  const Icon = current.icon;

  const [open, setOpen] = React.useState(false);

  const handlePick = (stepId) => {
    setOpen(false);
    onStepChange?.(stepId);
  };

  return (
    <div
      className="pos-step-nav-mobile sticky top-[57px] z-30 border-b border-white/10 bg-[#111c2e]"
      data-testid={testId}
    >
      <div className="px-3 py-2.5">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex w-full items-center justify-between text-left"
          data-testid={`${testId}-summary`}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-amber-500/20 text-amber-400">
              {Icon ? <Icon size={16} /> : <span className="text-sm font-bold">{currentIndex + 1}</span>}
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-slate-400">
                Step {currentIndex + 1} of {total}
              </div>
              <div className="truncate text-sm font-semibold text-white">{current.name || ""}</div>
            </div>
          </div>
          <ChevronDown
            size={18}
            className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>

        {/* Progress bar */}
        <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-white/5">
          <div
            className="h-full bg-gradient-to-r from-amber-400 to-emerald-400 transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Expandable step picker */}
        {open && (
          <div
            className="mt-3 grid grid-cols-2 gap-2 rounded-lg border border-white/10 bg-[#0a1628] p-2"
            data-testid={`${testId}-list`}
          >
            {steps.map((s, idx) => {
              const sIdx = stepOrder.indexOf(s.id);
              const reached = sIdx <= currentIndex;
              const active = s.id === currentStep;
              const StepIcon = s.icon;
              return (
                <button
                  key={String(s.id)}
                  type="button"
                  disabled={!reached}
                  onClick={() => handlePick(s.id)}
                  className={`flex items-center gap-2 rounded-md px-2 py-2 text-xs transition ${
                    active
                      ? "bg-amber-500 text-white"
                      : reached
                      ? "bg-white/5 text-slate-200 hover:bg-white/10"
                      : "bg-transparent text-slate-600"
                  }`}
                  data-testid={`${testId}-jump-${s.id}`}
                >
                  {StepIcon ? <StepIcon size={14} /> : <span>{idx + 1}.</span>}
                  <span className="truncate">{s.name}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileStepper;
